import { isEmpty, times } from 'lodash';
import PropTypes from 'prop-types';
import React, { PureComponent } from 'react';
import { FormattedMessage } from 'react-intl';

import CreateResponsiveStyledComponent from '../../mixins/CreateResponsiveStyledComponent';
import getExistingPatientUrl from '../utils/getExistingPatientUrl';
import CreateTimesGridContent from './CreateTimesGridContent';
import GovernmentInsuranceNotice from './GovernmentInsuranceNotice';

import {
    COMPONENT_SIZE_DEFNS,
    TIMESGRID_DESKTOP_CONFIG,
    SIZE_TO_NUM_COLS,
} from '../constants/ViewConstants';

import * as styled from './content-primitives';

class DesktopTimesGridContent extends PureComponent {
    static propTypes = {
        loading: PropTypes.bool,
        numRowsOnLoad: PropTypes.number.isRequired,
        onClickPhoneNumber: PropTypes.func,
        phoneNumber: PropTypes.string,
        profileUrl: PropTypes.string,
        linkTarget: PropTypes.string,
        showExistingPatientLink: PropTypes.bool,
        theme: PropTypes.shape({
            components: PropTypes.shape({
                AppointmentPicker: PropTypes.shape({
                    config: PropTypes.shape({
                        showExistingPatientsLink: PropTypes.bool,
                        showPhoneNumberIfNoAvailability: PropTypes.bool,
                    }),
                }),
            }),
        }),
        isWellGuideView: PropTypes.bool,
    };

    static defaultProps = {
        linkTarget: '_top',
    }

    render() {
        return (
            <styled.DesktopWrapper data-test="ap-desktop-content">
                {this.renderGrid()}
                {this.maybeRenderEntireTimesGridMessageArea()}
            </styled.DesktopWrapper>
        );
    }

    renderGrid() {
        const numColsToDisplay = this.getNumColsToDisplay();

        return (
            <div data-test="ap-desktop-content-grid">
                {
                    this.props.loading
                        ? this.renderLoadingIndicators(numColsToDisplay)
                        : this.renderGridRows(numColsToDisplay)
                }
            </div>
        );
    }

    renderLoadingIndicators(numCols) {
        return times(this.props.numRowsOnLoad, rowIndex => (
            <styled.DesktopGridRow
                key={rowIndex}
                data-test="ap-desktop-content-grid-row"
            >
                {this.renderColumnHolder()}

                {times(numCols, colIndex => {
                    return (
                        <styled.DesktopGridCell
                            key={colIndex}
                            data-test="ap-desktop-content-grid-cell-placeholder"
                            sizeId={this.props.sizeId}
                        >
                            <styled.DesktopLoadingSlot>
                                <span>{String.fromCharCode(8212)}</span>
                            </styled.DesktopLoadingSlot>
                        </styled.DesktopGridCell>
                    );
                })}

                {this.renderColumnHolder()}
            </styled.DesktopGridRow>
        ));
    }

    renderGridRows(numCols) {
        return this.props.times.map((gridRow, rowIndex) => (
            <styled.DesktopGridRow
                key={rowIndex}
                data-test="ap-desktop-content-grid-row"
            >
                {this.renderColumnHolder()}

                {gridRow.times.map((time, colIndex) => {
                    if (colIndex < numCols) {
                        return this.renderGridRow(time, colIndex, rowIndex);
                    }

                    return null;
                })}

                {this.renderColumnHolder()}
            </styled.DesktopGridRow>
        ));
    }

    renderGridRow(time, colIndex) {
        const slotID = time.id;
        const hasValidSlotID = !isEmpty(slotID);
        const hasMoreButton = !!time.isMoreBtn;

        let SlotElement;

        if (hasValidSlotID) {
            SlotElement = styled.DesktopSlot;
        } else if (hasMoreButton) {
            SlotElement = styled.DesktopMoreButton;
        } else {
            SlotElement = styled.DesktopEmptySlot;
        }

        return (
            <styled.DesktopGridCell
                key={time.id || colIndex}
                data-test="ap-desktop-content-grid-cell"
                sizeId={this.props.sizeId}
            >
                {this.renderSlot(SlotElement, time)}
            </styled.DesktopGridCell>
        );
    }

    renderSlot(SlotElement, time) {
        const slotID = time.id;
        const slotDateTime = time.datetime;

        switch (SlotElement) {
            case styled.DesktopSlot: {
                return (
                    <SlotElement
                        data-id={slotID}
                        data-prod-guids="timeslot-desktop:[4c2b7f86,cfa074a0,d1d2f34b,46ac31b8]"
                        data-test={`ap-desktop-content-timeslot-${slotID}`}
                        href={time.url}
                        onClick={this.handleTimeInteract}
                        rel="nofollow"
                        target={this.props.linkTarget}
                    >
                        <span>{slotDateTime}</span>
                    </SlotElement>
                );
            }

            case styled.DesktopEmptySlot: {
                return (
                    <SlotElement
                        data-id={slotID}
                        data-test="ap-desktop-content-timeslot-empty"
                    >
                        <span>{String.fromCharCode(8212)}</span>
                    </SlotElement>
                );
            }

            case styled.DesktopMoreButton: {
                return (
                    <SlotElement
                        data-has-more-button
                        data-id={slotID}
                        data-test="ap-desktop-content-more-times"
                        onClick={this.handleTimeInteract}
                    >
                        <span>more</span>
                    </SlotElement>
                );
            }
        }
    }

    renderColumnHolder() {
        return (
            <styled.DesktopNavigationPlaceholderColumn />
        );
    }

    maybeRenderEntireTimesGridMessageArea() {
        if (this.props.showEntireTimesGridMessage && !this.props.loading) {
            return (
                <styled.DesktopNoTimesView isWellGuideView={this.props.isWellGuideView} >
                    {this.renderEntireTimesGridMessage()}
                    {this.maybeRenderExistingPatientLink()}
                </styled.DesktopNoTimesView>
            );
        }
    }

    renderEntireTimesGridMessage() {
        const { defaultMessages } = this.props.theme.components.AppointmentPicker;

        if (this.props.showGovernmentInsuranceNotice) {
            return (
                <styled.DesktopNonBookableMessage
                    key="government-insurance-notice"
                    data-test="ap-desktop-content-government-insurance-notice"
                >
                    <GovernmentInsuranceNotice />
                </styled.DesktopNonBookableMessage>
            );
        } else if (this.props.showNextTimeAvailable) {
            return (
                <styled.DesktopNextAvailabilityLink
                    key="next-availability"
                    data-date={this.props.firstDateWithTimeslotsDataStr}
                    data-prod-guids="next-availability-desktop:[4c2b7f86,cfa074a0,d1d2f34b,46ac31b8]"
                    data-test="ap-desktop-content-go-to-day-with-avail-btn"
                    onClick={this.handleNextAvailClick}
                >
                    <FormattedMessage
                        defaultMessage={defaultMessages.NextAvailability}
                        id="AppointmentPicker.DesktopView.Content.NextAvailability"
                        values={{ nextAvailableDate: this.props.nextAvailDate }}
                    />
                </styled.DesktopNextAvailabilityLink>
            );
        } else if (
            this.props.theme.components.AppointmentPicker.config.showPhoneNumberIfNoAvailability
            && this.props.phoneNumber) {
            return (
                <styled.DesktopPhoneNumberLink
                    key="phone"
                    data-test="ap-desktop-content-phone-number-link"
                    href={`tel:${this.props.phoneNumber.replace(/\D/g, '')}`}
                    onClick={this.props.onClickPhoneNumber}
                >
                    <FormattedMessage
                        defaultMessage={defaultMessages.NoAvailabilityWithCta}
                        id="AppointmentPicker.DesktopView.Content.NoAvailabilityWithCta"
                        values={{ phoneNumber: this.props.phoneNumber }}
                    />
                </styled.DesktopPhoneNumberLink>
            );
        }

        return (
            <styled.DesktopNonBookableMessage
                key="no-availability"
                data-test="ap-desktop-content-preview-doctor-timeslot"
            >
                <FormattedMessage
                    defaultMessage={defaultMessages.NoAvailability}
                    id="AppointmentPicker.DesktopView.Content.NoAvailability"
                />
            </styled.DesktopNonBookableMessage>
        );
    }

    maybeRenderExistingPatientLink() {
        const { defaultMessages, config } = this.props.theme.components.AppointmentPicker;

        if (this.props.profileUrl && this.props.showExistingPatientLink && config.showExistingPatientsLink) {
            const urlWithExistingPatientFlag = getExistingPatientUrl(this.props.profileUrl);

            return (
                <styled.DesktopExistingPatientLink
                    data-test="ap-desktop-content-existing-patient-link"
                    href={urlWithExistingPatientFlag}
                >
                    <styled.DesktopExistingPatientLinkText>
                        <FormattedMessage
                            defaultMessage={defaultMessages.ViewExistingPatientAvailability}
                            id="AppointmentPicker.DesktopView.Content.ViewExistingPatientAvailability"
                        />
                    </styled.DesktopExistingPatientLinkText>

                    <styled.DesktopExistingPatientLinkArrow
                        color="currentcolor"
                        direction="right"
                        height={10}
                    />
                </styled.DesktopExistingPatientLink>
            );
        }
    }

    getNumColsToDisplay() {
        return SIZE_TO_NUM_COLS[this.props.sizeId];
    }

    handleNextAvailClick = e => this.props.goToDayWithAvailBtnClicked(
        this.getNumColsToDisplay(), this.props.nextAvailDate, e
    );

    handleTimeInteract = e => {
        this.props[e.type === 'mouseup' ? 'onTimeMouseUp' : 'onTimeClicked'](
            !!e.currentTarget.getAttribute('data-has-more-button'), e.currentTarget.getAttribute('href'), e
        );
    };
}

/* eslint-disable babel/new-cap */
export default (
    CreateResponsiveStyledComponent(COMPONENT_SIZE_DEFNS)(
        CreateTimesGridContent(TIMESGRID_DESKTOP_CONFIG)(
            DesktopTimesGridContent
        )
    )
);
